import { useCallback, useEffect, useRef, useState } from 'react';

// ============================================
// Slot battles — shared by the /slot-battles page, the admin console on it,
// and the /widget/battles OBS source. The backend (backend/slot-battles.js)
// pushes the featured battle (the live one, else the last finished) over
// server-sent events on every change; this falls back to polling while the
// stream is down, and EventSource reconnects on its own.
// ============================================

let API_URL = 'https://api.snutzy.gg';
let getToken = () => null;
// The page and widget hand in App's BACKEND_API_URL / token getter once.
export function setBattleApi(apiUrl, tokenGetter) {
  if (apiUrl) API_URL = apiUrl;
  if (tokenGetter) getToken = tokenGetter;
}

export async function battleApi(path, { method = 'GET', body } = {}) {
  const token = getToken?.();
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

export const adminBattle = (id) => battleApi(`/api/admin/battles/${id}`);
export const battleAction = (path, body, method = 'POST') =>
  battleApi(path, { method, body: method === 'DELETE' || method === 'GET' ? undefined : (body || {}) });

// ---------- palette — the snutzy brand (.claude/skills/snutzy-brand) ----------
// Purple + white carry the design. Gold is reserved for money figures and the
// call-to-action button, never a general accent.
export const C = {
  bg: '#050507',
  violet: '#a855f7',
  violetHi: '#c084fc',
  violetDeep: '#7c3aed',
  lav: '#d8b4fe',
  lavLight: '#e9d5ff',
  gold: '#fbbf24',
  goldDeep: '#f59e0b',
  onGold: '#1c1205',
  silver: '#cbd5e1',
  bronze: '#d97706',
  green: '#22c55e',
  mint: '#4ade80',
  loss: '#f87171',
  text: '#ffffff',
  bright: '#e4e4e7',
  soft: '#d4d4d8',
  muted: '#a1a1aa',
  dim: '#71717a',
  faint: '#52525b',
  line: 'rgba(255,255,255,0.08)',
  violetLine: 'rgba(168,85,247,0.28)',
  panel: 'rgba(255,255,255,0.03)',
  raised: 'rgba(255,255,255,0.05)',
  font: "'Outfit', sans-serif",
  display: "'Unbounded', 'Outfit', sans-serif",
  headline: 'linear-gradient(135deg, #fff 30%, #d8b4fe 65%, #a855f7 95%)',
};
export const MEDAL = { 1: C.gold, 2: C.silver, 3: C.bronze };
// Side a and side b of every fight: violet corner vs silver corner.
export const SIDE = { a: C.violet, b: C.silver };
export const SIDE_NAME = { a: 'Violet', b: 'Silver' };

// How a fight was won, in slot-win terms — the ratio of the winner's score to
// the loser's. A near-tie gets its own call-out.
export function winTier(win, lose) {
  if (win == null || lose == null) return null;
  const r = lose > 0 ? win / lose : win > 0 ? Infinity : 1;
  if (r >= 10) return { label: 'Epic win', level: 3 };
  if (r >= 5) return { label: 'Mega win', level: 2 };
  if (r >= 2) return { label: 'Big win', level: 1 };
  if (r < 1.1) return { label: 'Nail-biter', level: 0 };
  return null;
}

export const FORMAT_META = {
  tournament: {
    label: 'Tournament',
    short: 'Tournament',
    icon: '🏆',
    tag: 'knockout bracket',
    blurb: 'Chat fills a knockout bracket. Each match is one bonus buy per side — best multi moves on, the last one standing takes the cup.',
  },
  arena: {
    label: 'Team Arena',
    short: 'Arena',
    icon: '⚔️',
    tag: 'squads of 1 · 2 · 3',
    blurb: 'Squads drawn from chat go head to head — solo, duos or trios. Every bonus is bought on stream and the squad with the better average multi shares the prize.',
  },
  versus: {
    label: 'Snutzy vs Chat',
    short: 'Vs Chat',
    icon: '🎙️',
    tag: 'take on the streamer',
    blurb: "Snutzy picks his slot, chat gets drawn to take him on — one-on-one, or his squad against yours. Out-multi him and the prize is chat's.",
  },
};

export const FORMATS = ['tournament', 'arena', 'versus'];

// Chat's way out of the pool (until rolled). Mirrors EXIT_COMMAND in
// backend/slot-battles.js — keep the two identical.
export const EXIT_COMMAND = '!exit';

export const isLive = (b) => !!b && (b.status === 'lobby' || b.status === 'live');

export function useBattleFeed() {
  const [state, setState] = useState(undefined); // undefined = loading, null = nothing
  const [connected, setConnected] = useState(false);

  const load = useCallback(
    () => battleApi('/api/battles/live').then((d) => setState(d.state ?? null)).catch(() => {}),
    []
  );

  useEffect(() => {
    let alive = true;
    let poll = null;
    const startPoll = () => { if (!poll) poll = setInterval(() => { if (alive) load(); }, 5000); };
    const stopPoll = () => { if (poll) { clearInterval(poll); poll = null; } };
    load();
    let es = null;
    if (typeof EventSource !== 'undefined') {
      es = new EventSource(`${API_URL}/api/battles/stream`);
      es.onopen = () => { if (alive) { setConnected(true); stopPoll(); } };
      es.onmessage = (e) => {
        if (!alive) return;
        try { setState(JSON.parse(e.data)); } catch { /* keep the last good state */ }
      };
      es.onerror = () => { if (alive) { setConnected(false); startPoll(); } };
    } else {
      startPoll();
    }
    return () => { alive = false; if (es) es.close(); stopPoll(); };
  }, [load]);

  return { state, connected, reload: load };
}

// The server's clock, measured NTP-style: a few round trips to
// /api/battles/time, keeping the one with the shortest round trip and
// assuming the server stamped it halfway through. Accurate to a few ms, which
// is what lets the page's and the widget's roll reels move in lockstep.
// Shared by every hook on the page; re-measured every minute.
let syncedOffset = null;
let syncing = null;
function syncClock() {
  if (syncing) return syncing;
  syncing = (async () => {
    let best = null;
    for (let i = 0; i < 5; i++) {
      try {
        const t0 = Date.now();
        const r = await fetch(`${API_URL}/api/battles/time`, { cache: 'no-store' });
        const t1 = Date.now();
        if (!r.ok) break;
        const { now } = await r.json();
        const rtt = t1 - t0;
        if (!best || rtt < best.rtt) best = { rtt, offset: now - (t0 + t1) / 2 };
      } catch { break; }
    }
    if (best) syncedOffset = best.offset;
    syncing = null;
  })();
  return syncing;
}
if (typeof window !== 'undefined') setInterval(() => { if (syncedOffset != null) syncClock(); }, 60000);

// Ages are measured on the server's clock, so an OBS PC or a viewer with a
// wrong clock still gets the drama right. Until the sync lands, each
// payload's server_time (stamped at send) stands in.
export function useServerClock(state) {
  const offset = useRef(0);
  const last = useRef(null);
  if (state?.server_time && state.server_time !== last.current) {
    last.current = state.server_time;
    offset.current = Date.parse(state.server_time) - Date.now();
  }
  useEffect(() => { syncClock(); }, []);
  return useCallback((iso) => (iso ? Date.now() + (syncedOffset ?? offset.current) - Date.parse(iso) : Infinity), []);
}

// The roll: every rolled seat spins a reel through the pool and lands on its
// player — side a first, side b a beat later, a team's players a step apart.
// Landing times count from the match's rolled_at on the server clock, so the
// page and the widget land together. Someone who shows up late still gets at
// least `minSpin` of reel. The streamer's own pick in a versus fight isn't
// rolled, so it doesn't spin — nor do the teammates he handpicks.
export const ROLL = { land: 4.2, sideGap: 0.9, teamGap: 0.35, minSpin: 1.8, freshMs: 12000 };

// → { spins: { [playId]: seconds }, totals, sides: { a, b }, end } for a fresh
// roll, else null. `sides` is when each side's last reel stops.
export function rollSchedule(match, elapsedMs) {
  if (!match?.rolled_at || match.status === 'done' || !(elapsedMs < ROLL.freshMs)) return null;
  const rolled = match.plays.filter((p) => !p.is_streamer && !p.handpicked);
  if (!rolled.length) return null;
  const aSpins = rolled.some((p) => p.side === 'a');
  const landAt = (p) => ROLL.land + (p.side === 'b' && aSpins ? ROLL.sideGap : 0) + p.ord * ROLL.teamGap;
  const first = Math.min(...rolled.map(landAt));
  const shift = Math.min(Math.max(0, elapsedMs / 1000), first - ROLL.minSpin);
  const spins = {};
  const totals = {};
  const sides = { a: 0, b: 0 };
  for (const p of rolled) {
    spins[p.id] = landAt(p) - shift;
    totals[p.id] = landAt(p);
    sides[p.side] = Math.max(sides[p.side], spins[p.id]);
  }
  return { spins, totals, sides, end: Math.max(sides.a, sides.b) };
}

export const slotLabel = (slot) => slot?.name || slot?.raw || null;

export function fmtMulti(x) {
  if (x == null || !Number.isFinite(x)) return '—';
  if (x >= 1000) return `${Math.round(x).toLocaleString('en-US')}x`;
  if (x >= 100) return `${x.toFixed(1)}x`;
  return `${x.toFixed(2)}x`;
}

// green at or above break-even, red below — semantic money colours
export const multiColor = (x) => (x == null ? C.dim : x >= 1 ? C.mint : C.loss);

export const fmtAmount = (n) =>
  n == null ? '—' : `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

export const totalRounds = (size) => Math.round(Math.log2(size || 2));

export function roundLabel(round, size) {
  const total = totalRounds(size);
  if (round === total) return 'Final';
  if (round === total - 1) return 'Semi-finals';
  if (round === total - 2) return 'Quarter-finals';
  return `Round of ${2 ** (total - round + 1)}`;
}

// The 3rd-place match: the semi-final losers, in the final's round beside it
// at idx 1. Mirrors isThirdPlace() in backend/slot-battles.js.
export const isThirdPlace = (match, battle) =>
  !!battle?.third_place && match.round === totalRounds(battle.bracket_size) && match.idx === 1;

export const matchLabel = (match, battle) =>
  (isThirdPlace(match, battle) ? 'Bronze match' : roundLabel(match.round, battle.bracket_size));

// Who still has a match to play in a tournament — pick windows count these.
// Not knocked out, plus the semi-final losers while their 3rd-place match is
// pending (out of the title race, not out of the tournament).
export function playersInPlay(state) {
  const third = state.matches.find((m) => isThirdPlace(m, state.battle));
  const inThird = new Set(third && third.status !== 'done' ? third.plays.map((p) => p.player_id) : []);
  return state.players.filter((p) => !p.replaced && (p.eliminated_round == null || inThird.has(p.id)));
}

export const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

export function sidesOf(match) {
  const a = match.plays.filter((p) => p.side === 'a').sort((x, y) => x.ord - y.ord);
  const b = match.plays.filter((p) => p.side === 'b').sort((x, y) => x.ord - y.ord);
  return { a, b };
}

// A 2v2/3v3 side is named after the first player rolled into it
// ("Team notpa1n") — plays come ordered by roll order (ord).
export const teamName = (plays, fallback) => (plays[0]?.name ? `${plays[0].name}'s squad` : fallback);

// Mean multi of a side, live — a half-entered 3v3 still has a running
// average worth showing.
export function sideScore(plays) {
  const xs = plays.map((p) => p.multi).filter((x) => x != null);
  if (!xs.length) return null;
  return xs.reduce((s, x) => s + x, 0) / xs.length;
}

export const focusedMatch = (state) =>
  state?.battle?.focus_match_id ? state.matches.find((m) => m.id === state.battle.focus_match_id) || null : null;

// What an arena / versus session advertises before and between fights:
// "3v3 fights · $300 per fight, split 3 ways". Null parts are left out.
export function fightTerms(battle) {
  if (!battle || battle.format === 'tournament') return null;
  const size = battle.default_team_size || 1;
  const prize = battle.default_prize > 0 ? battle.default_prize : null;
  const squad = ['', 'Solo', 'Duos', 'Trios'][size] || `${size}s`;
  if (battle.format === 'versus') {
    const who = battle.streamer_name || 'Snutzy';
    if (size > 1) return `${squad} · chat's squad vs ${who}'s${prize ? ` · ${fmtAmount(prize)} split ${size} ways` : ''}`;
    return prize ? `Out-multi ${who} for ${fmtAmount(prize)}` : null;
  }
  if (!prize) return `${squad} battles`;
  return size > 1 ? `${squad} battles · ${fmtAmount(prize)} a battle, split ${size} ways` : `${squad} battles · ${fmtAmount(prize)} a battle`;
}

// A versus fight's side names: the streamer vs the viewer in a 1v1, "Team
// Snutzy" vs "Team <first rolled>" in a 2v2/3v3.
export function versusLabels(match, battle) {
  const who = battle.streamer_name || 'Snutzy';
  const chat = match.plays.filter((p) => p.side === 'b').sort((p, q) => p.ord - q.ord);
  if ((match.team_size || 1) > 1) return { a: `${who}'s squad`, b: teamName(chat, 'Chat squad') };
  return { a: who, b: chat[0]?.name || 'Viewer' };
}

// How many ways a fight's prize splits if `side` wins: every viewer on it.
// A versus house win pays nobody — not the streamer, not his teammates.
export function prizeShares(match, battle, side) {
  return battle.format === 'versus' && side === 'a' ? 0 : match.team_size || 1;
}

export function versusScore(state) {
  let house = 0;
  let chat = 0;
  for (const m of state?.matches || []) {
    if (m.status !== 'done') continue;
    if (m.winner_side === 'a') house++;
    else chat++;
  }
  return { house, chat };
}

export const tournamentLabels = (match, battle) => {
  const { a, b } = sidesOf(match);
  const third = isThirdPlace(match, battle);
  return {
    a: a[0]?.name || 'TBD',
    b: b[0]?.name || 'TBD',
    round: matchLabel(match, battle),
    // the final's round holds two matches with the 3rd-place one on
    heading: third || match.round === totalRounds(battle.bracket_size)
      ? matchLabel(match, battle)
      : `${roundLabel(match.round, battle.bracket_size)} · match ${match.idx + 1}`,
  };
};

export const fightLabels = (match, battle) => {
  if (battle.format === 'tournament') return tournamentLabels(match, battle);
  const { a, b } = sidesOf(match);
  if (battle.format === 'versus') return versusLabels(match, battle);
  if (match.team_size === 1) return { a: a[0]?.name || 'Violet', b: b[0]?.name || 'Silver' };
  return { a: teamName(a, 'Violet squad'), b: teamName(b, 'Silver squad') };
};

// Query string helpers (the app routes on the path; the page keeps its tab
// and an opened battle in ?mode= / ?battle= so links can point at them).
export function getQuery(key) {
  try { return new URLSearchParams(window.location.search).get(key); } catch { return null; }
}
export function setQuery(patch) {
  try {
    const p = new URLSearchParams(window.location.search);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null) p.delete(k); else p.set(k, v);
    }
    const qs = p.toString();
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
  } catch { /* ignore */ }
}

export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/London' }) : '');
